import { useState, useEffect, useCallback } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { customerApi } from '../../services/api'
import ProgressIndicator from './ProgressIndicator'
import Step1Welcome from './steps/Step1Welcome'
import Step2AddContent from './steps/Step2AddContent'
import Step3Preview from './steps/Step3Preview'
import Step4Customize from './steps/Step4Customize'
import Step5Install from './steps/Step5Install'

export default function OnboardingWizard({ user, apiKey, onComplete }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [stepData, setStepData] = useState({})

  useEffect(() => {
    fetchOnboardingStatus()
  }, [])

  const fetchOnboardingStatus = async () => {
    try {
      const response = await customerApi.get('/customer/onboarding/status')
      const { current_step, onboarding_completed } = response.data
      if (onboarding_completed) {
        onComplete()
        return
      }
      if (current_step) {
        setCurrentStep(current_step)
      }
    } catch (err) {
      console.error('Failed to fetch onboarding status:', err)
    } finally {
      setLoading(false)
    }
  }

  const completeStep = useCallback(async (step, data = {}) => {
    try {
      const response = await customerApi.post('/customer/onboarding/complete-step', { step, data })
      if (response.data.onboarding_completed) {
        onComplete()
      }
      return response.data
    } catch (err) {
      console.error('Failed to complete step:', err)
    }
  }, [onComplete])

  const handleNext = useCallback(async (data = {}) => {
    setStepData(prev => ({ ...prev, [currentStep]: data }))
    await completeStep(currentStep, data)
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1)
    }
  }, [currentStep, completeStep])

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1)
    }
  }, [currentStep])

  const handleSkip = async () => {
    try {
      await customerApi.post('/customer/onboarding/skip')
      onComplete()
    } catch (err) {
      console.error('Failed to skip onboarding:', err)
      onComplete()
    }
  }

  const handleSkipStep = useCallback(async () => {
    await completeStep(currentStep, { skipped: true })
    if (currentStep < 5) {
      setCurrentStep(prev => prev + 1)
    }
  }, [currentStep, completeStep])

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
      </div>
    )
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Welcome user={user} onNext={handleNext} />
      case 2:
        return <Step2AddContent onNext={handleNext} onSkipStep={handleSkipStep} />
      case 3:
        return <Step3Preview apiKey={apiKey} onNext={handleNext} />
      case 4:
        return <Step4Customize onNext={handleNext} />
      case 5:
        return <Step5Install apiKey={apiKey} onComplete={onComplete} />
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Skip button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 text-slate-400 hover:text-slate-600 transition-colors"
          title="Skip onboarding"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Progress indicator */}
        <div className="px-8 pt-8 pb-4">
          <ProgressIndicator currentStep={currentStep} totalSteps={5} />
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto px-8 py-4">
          {renderStep()}
        </div>

        {/* Navigation buttons */}
        {currentStep > 1 && currentStep < 5 && (
          <div className="flex items-center justify-between px-8 py-4 border-t border-slate-100">
            <button
              onClick={handleBack}
              className="px-6 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
            >
              Back
            </button>
            <div />
          </div>
        )}
      </div>
    </div>
  )
}
